// eslint-disable-next-line no-restricted-imports
import { DEFAULT_ICON_SIZE } from '@/features/floor-plan/hooks/useFloorChart'
import { HYDRATE } from 'next-redux-wrapper'

import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'

import {
  Action,
  DeviceAssociationStatus,
  DeviceType,
} from '@/graphql/generated/schemas'

import type { AppState } from '../store'
import {
  CreatedAtFilter,
  CreatedAtRangeFilter,
  Filter,
  MultiFilter,
  SubmittedAtRangeFilter,
} from '../types/filters'
import type { HydratePayload } from '../types/hydrate'
import type {
  DevicePayload,
  EmergencyCallPayload,
  FacilityDrawer,
  FloorTabCounts,
} from './uiSlice.types'

const defaultAssociationStatusFilter = [
  { value: DeviceAssociationStatus.Unassociated, label: 'Unassociated' },
  { value: DeviceAssociationStatus.Associated, label: 'Associated' },
  { value: DeviceAssociationStatus.GridDrawn, label: 'Grids Drawn' },
  {
    value: DeviceAssociationStatus.TailgateEnabled,
    label: 'Tailgating Enabled',
  },
]

export type IncidentDrawerNavigationIncident = {
  id: string
  displayId: string
}

export type IncidentDrawerNavigationContext = {
  isFromIncidentsTable: boolean
  incidents: IncidentDrawerNavigationIncident[]
}

export interface UIStateIProps {
  isSideNavOpen: boolean
  isEditMode: boolean
  hasConfirmedEditMode: boolean
  userSettingsLoaded: boolean
  // Ids of first-time-use tours the user has completed (see features/first-time-use).
  completedFtuIds: string[]
  facility: {
    drawerOpen?: FacilityDrawer
    placedDeviceCount?: number
    incidentsCount?: number
    floorCount?: number
    checkpointCount?: number
    tourPlansCount?: number
    // Floor plan ID is key (string), value is the size that icons should be (number)
    iconSizes: object
  }
  facilities: {
    filters: {
      ids: MultiFilter
      createdAtRange: CreatedAtFilter
      hasIncidents: Filter<boolean>
    }
  }
  devices: {
    pageSize: number
    filters: {
      facilityIds: MultiFilter
      floorIds: MultiFilter
      createdAtRange: CreatedAtFilter
      hasIncidents: Filter<boolean>
      deviceStatus: MultiFilter
      deviceType: MultiFilter
      externalAccountIds: MultiFilter
      labelIds: MultiFilter
      associationStatus: MultiFilter
    }
  }
  incidentRules: {
    pageSize: number
    filters: {
      nameLike: Filter
      facilityIds: MultiFilter
      actions: MultiFilter<Action>
      status: Filter<boolean>
    }
  }
  outstandingIncidents: {
    filters: {
      facilities: MultiFilter
      deviceType: MultiFilter
      incidentTypes: MultiFilter
      guards: MultiFilter
      operators: MultiFilter
      timeOrder: MultiFilter
    }
    pageSize: number
  }
  isDarkMode: boolean
  notifications: {
    showUnread: boolean
    incidentAudioAlert: boolean
  }
  sops: {
    filters: {
      nameLike: Filter
      facilityId: Filter
      createdAtRange: CreatedAtFilter
      creatorId: Filter
      type: Filter
    }
    pageSize: number
  }
  gatewayDevices: {
    pageSize: number
    filters: {
      nameLike: Filter
    }
  }
  guardTours: {
    pageSize: number
    filters: {
      facilityIds: MultiFilter
      guardIds: MultiFilter
      status: MultiFilter
      submittedAtRange: SubmittedAtRangeFilter
    }
  }
  aiOperatorHub: {
    filters: {
      createdAtRange: CreatedAtRangeFilter
    }
  }
  drawers: {
    incident: {
      id: string
      isOpen: boolean
      isVideoPlaying: boolean
      navigationContext: IncidentDrawerNavigationContext
    }
    incidentTraceLog: {
      id: string
      isOpen: boolean
      highlightedEvent: {
        detectedAt: string
        externalId: string
      } | null
      // Device to filter the Device tab to (overrides the incident's primary
      // device) and whether to open directly on the Device tab. Set by the
      // Reasoning Trail "View access logs" deep-link; null/false otherwise.
      deviceId: string | null
      shouldForceDeviceTab: boolean
    }
    device: {
      id: string
      type: DeviceType
      isOpen: boolean
    }
    sop: {
      id: string
      isOpen: boolean
    }
    sopIncident: {
      id: string | null
      isOpen: boolean
      sopNameLike: string | null
    }
    guardTour: {
      id: string
      isOpen: boolean
    }
    incidentWorkflow: {
      isOpen: boolean
    }
    incidentMapPanel: {
      isOpen: boolean
    }
  }
  emergencyCall: {
    isOpen: boolean
    isExpanded: boolean
    isMuted: boolean
    incident: {
      id: string
      displayId: string
      facilityName: string
      emergencyNumber: string
    }
    filters: {
      displayIdLike: Filter
    }
    pageSize: number
  }
  cases: {
    filters: {
      nameOrDisplayIdLike: Filter
      owner: Filter
      status: Filter[]
    }
    pageSize: number
  }
}

export const initialState: UIStateIProps = {
  isSideNavOpen: true,
  isEditMode: false,
  hasConfirmedEditMode: false,
  userSettingsLoaded: false,
  completedFtuIds: [],
  // Dark is the default theme (ENG-33713). Exposure is still gated on
  // FeatureFlag.showDarkModeToggle in useLightMode — see that hook.
  isDarkMode: true,
  facility: {
    drawerOpen: 'devices',
    placedDeviceCount: null,
    incidentsCount: null,
    floorCount: null,
    checkpointCount: null,
    iconSizes: {},
  },
  facilities: {
    filters: {
      ids: null,
      createdAtRange: null,
      hasIncidents: null,
    },
  },
  devices: {
    filters: {
      facilityIds: null,
      floorIds: null,
      createdAtRange: null,
      hasIncidents: null,
      deviceStatus: null,
      deviceType: null,
      externalAccountIds: null,
      labelIds: null,
      associationStatus: defaultAssociationStatusFilter,
    },
    pageSize: 25,
  },
  incidentRules: {
    filters: {
      nameLike: null,
      facilityIds: null,
      actions: null,
      status: null,
    },
    pageSize: 25,
  },
  outstandingIncidents: {
    filters: {
      facilities: [],
      deviceType: [],
      incidentTypes: [],
      guards: [],
      operators: [],
      timeOrder: [],
    },
    pageSize: 25,
  },
  notifications: {
    showUnread: false,
    incidentAudioAlert: false,
  },
  sops: {
    filters: {
      nameLike: null,
      facilityId: null,
      createdAtRange: null,
      creatorId: null,
      type: null,
    },
    pageSize: 25,
  },
  cases: {
    filters: {
      nameOrDisplayIdLike: null,
      owner: null,
      status: null,
    },
    pageSize: 25,
  },
  gatewayDevices: {
    pageSize: 25,
    filters: {
      nameLike: null,
    },
  },
  guardTours: {
    pageSize: 25,
    filters: {
      facilityIds: [],
      guardIds: [],
      status: [],
      submittedAtRange: null,
    },
  },
  aiOperatorHub: {
    filters: {
      createdAtRange: null,
    },
  },
  drawers: {
    incident: {
      id: null,
      isOpen: false,
      isVideoPlaying: false,
      navigationContext: {
        isFromIncidentsTable: false,
        incidents: [],
      },
    },
    incidentTraceLog: {
      id: null,
      isOpen: false,
      highlightedEvent: null,
      deviceId: null,
      shouldForceDeviceTab: false,
    },
    device: {
      id: null,
      type: null,
      isOpen: false,
    },
    sop: {
      id: null,
      isOpen: false,
    },
    sopIncident: {
      id: null,
      isOpen: false,
      sopNameLike: null,
    },
    guardTour: {
      id: null,
      isOpen: false,
    },
    incidentWorkflow: {
      isOpen: false,
    },
    incidentMapPanel: {
      isOpen: false,
    },
  },
  emergencyCall: {
    isOpen: false,
    isExpanded: true,
    isMuted: false,
    incident: {
      id: null,
      displayId: null,
      facilityName: null,
      emergencyNumber: null,
    },
    filters: {
      displayIdLike: null,
    },
    pageSize: 25,
  },
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // completedFtuIds is deliberately not carried over: the backend holds each
    // user's completions and hydrates them on login, and keeping the list here
    // would replay one user's completions into the next user's row on a shared
    // browser (see useFtuCompletionSync).
    resetUi: (state) => {
      return {
        ...initialState,
        hasConfirmedEditMode: state.hasConfirmedEditMode,
      }
    },
    toggleSideNavOpen: (state) => {
      state.isSideNavOpen = !state.isSideNavOpen
    },
    toggleEditMode: (state) => {
      state.isEditMode = !state.isEditMode
    },
    setVideoPlaying: (state, action: PayloadAction<boolean>) => {
      state.drawers.incident.isVideoPlaying = action.payload
    },
    toggleNotificationsUnread: (state) => {
      state.notifications.showUnread = !state.notifications.showUnread
    },
    setIncidentAudioAlert: (state, action: PayloadAction<boolean>) => {
      state.notifications.incidentAudioAlert = action.payload
    },
    setIsDarkMode: (state, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload
    },
    setUserSettingsLoaded: (state, action: PayloadAction<boolean>) => {
      state.userSettingsLoaded = action.payload
    },
    // Replace the whole set (used when hydrating from the backend, Phase 2).
    setCompletedFtuIds: (state, action: PayloadAction<string[]>) => {
      state.completedFtuIds = action.payload
    },
    // Mark a single FTU complete (idempotent union — used on tour completion).
    addCompletedFtuId: (state, action: PayloadAction<string>) => {
      if (!state.completedFtuIds.includes(action.payload)) {
        state.completedFtuIds.push(action.payload)
      }
    },
    // Union the backend's completed set into the local one. Local ids keep their
    // position; ids only the backend knows are appended in the backend's order.
    mergeCompletedFtuIds: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        if (!state.completedFtuIds.includes(id)) {
          state.completedFtuIds.push(id)
        }
      })
    },
    enableEditMode: (state) => {
      state.isEditMode = true
      state.hasConfirmedEditMode = true
    },
    openFacilityFloorsDrawer: (state) => {
      state.facility.drawerOpen = 'floors'
    },
    openFacilityDevicesDrawer: (state) => {
      state.facility.drawerOpen = 'devices'
    },
    openFacilityCheckpointsDrawer: (state) => {
      state.facility.drawerOpen = 'checkpoints'
    },
    openFacilityTourPlansDrawer: (state) => {
      state.facility.drawerOpen = 'tour-plans'
    },
    setFacilityTabBarCount: (state, action: PayloadAction<FloorTabCounts>) => {
      state.facility = {
        ...state.facility,
        ...action.payload,
      }
    },
    setFacilityIdsFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.facilities.filters.ids = action.payload
    },
    setFacilityCreatedAtFilter: (
      state,
      action: PayloadAction<CreatedAtFilter>
    ) => {
      state.facilities.filters.createdAtRange = action.payload
    },
    setFacilityHasIncidentsFilter: (
      state,
      action: PayloadAction<Filter<boolean>>
    ) => {
      state.facilities.filters.hasIncidents = action.payload
    },
    setDeviceFacilityIdsFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.devices.filters.facilityIds = action.payload
    },
    setDeviceFloorsIdsFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.devices.filters.floorIds = action.payload
    },
    setDeviceFacilityCreatedAtFilter: (
      state,
      action: PayloadAction<CreatedAtFilter>
    ) => {
      state.devices.filters.createdAtRange = action.payload
    },
    setDevicesHasIncidentsFilter: (
      state,
      action: PayloadAction<Filter<boolean>>
    ) => {
      state.devices.filters.hasIncidents = action.payload
    },
    setDeviceStatusFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.devices.filters.deviceStatus = action.payload
    },
    setDeviceTypeFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.devices.filters.deviceType = action.payload
    },
    setDeviceExternalAccountIdsFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.devices.filters.externalAccountIds = action.payload
    },
    setDeviceLabelIdsFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.devices.filters.labelIds = action.payload
    },
    setDeviceAssociationStatusFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.devices.filters.associationStatus = action.payload
    },
    setDevicesPageSize: (state, action: PayloadAction<number>) => {
      state.devices.pageSize = action.payload
    },
    setIncident: (state, action: PayloadAction<{ id: string }>) => {
      state.drawers.incident.id = action.payload.id
    },
    setOutstandingIncidentsFacilitiesFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.outstandingIncidents.filters.facilities = action.payload
    },
    setOutstandingIncidentsIncidentTypesFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.outstandingIncidents.filters.incidentTypes = action.payload
    },
    setOutstandingIncidentsDeviceTypeFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.outstandingIncidents.filters.deviceType =
        state.outstandingIncidents.filters.deviceType.length > 0
          ? action.payload.length === 0
            ? []
            : [action.payload[1]]
          : action.payload
    },
    setOutstandingIncidentsGuardsFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.outstandingIncidents.filters.guards = action.payload
    },
    setOutstandingIncidentsOperatorsFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.outstandingIncidents.filters.operators = action.payload
    },
    setOutstandingIncidentsTimeOrderFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.outstandingIncidents.filters.timeOrder =
        state.outstandingIncidents.filters.timeOrder.length > 0
          ? action.payload.length === 0
            ? []
            : [action.payload[1]]
          : action.payload
    },
    setAiOperatorHubCreatedAtFilter: (
      state,
      action: PayloadAction<CreatedAtRangeFilter>
    ) => {
      state.aiOperatorHub.filters.createdAtRange = action.payload
    },
    removeOutstandingIncidentFacility: (
      state,
      action: PayloadAction<string>
    ) => {
      state.outstandingIncidents.filters.facilities =
        state.outstandingIncidents.filters.facilities.filter(
          (i) => i.value !== action.payload
        )
    },
    removeOutstandingIncidentDeviceType: (
      state,
      action: PayloadAction<string>
    ) => {
      state.outstandingIncidents.filters.deviceType =
        state.outstandingIncidents.filters.deviceType.filter(
          (i) => i.value !== action.payload
        )
    },
    removeOutstandingIncidentIncidentTypes: (
      state,
      action: PayloadAction<string>
    ) => {
      state.outstandingIncidents.filters.incidentTypes =
        state.outstandingIncidents.filters.incidentTypes.filter(
          (i) => i.value !== action.payload
        )
    },
    removeOutstandingIncidentGuard: (state, action: PayloadAction<string>) => {
      state.outstandingIncidents.filters.guards =
        state.outstandingIncidents.filters.guards.filter(
          (i) => i.value !== action.payload
        )
    },
    removeOutstandingIncidentOperator: (
      state,
      action: PayloadAction<string>
    ) => {
      state.outstandingIncidents.filters.operators =
        state.outstandingIncidents.filters.operators.filter(
          (i) => i.value !== action.payload
        )
    },
    removeOutstandingIncidentTimeOrder: (
      state,
      action: PayloadAction<string>
    ) => {
      state.outstandingIncidents.filters.timeOrder =
        state.outstandingIncidents.filters.timeOrder.filter(
          (i) => i.value !== action.payload
        )
    },
    clearOutstandingIncidentFilters: (state) => {
      state.outstandingIncidents.filters =
        initialState.outstandingIncidents.filters
    },
    setOutstandingIncidentsPageSize: (state, action: PayloadAction<number>) => {
      state.outstandingIncidents.pageSize = action.payload
    },
    setSOPNameLikeFilter: (state, action: PayloadAction<Filter>) => {
      state.sops.filters.nameLike = action.payload
    },
    setCaseNameOrDisplayIdLike: (state, action: PayloadAction<Filter>) => {
      state.cases.filters.nameOrDisplayIdLike = action.payload
    },
    setCaseStatusFilter: (state, action: PayloadAction<Filter[]>) => {
      state.cases.filters.status = action.payload
    },
    setCaseOwnerFilter: (state, action: PayloadAction<Filter>) => {
      state.cases.filters.owner = action.payload
    },
    setSOPFacilityIdFilter: (state, action: PayloadAction<Filter>) => {
      state.sops.filters.facilityId = action.payload
    },
    setSOPCreatedAtFilter: (state, action: PayloadAction<CreatedAtFilter>) => {
      state.sops.filters.createdAtRange = action.payload
    },
    setSOPCreatorIdFilter: (state, action: PayloadAction<Filter>) => {
      state.sops.filters.creatorId = action.payload
    },

    setSOPTypeFilter: (state, action: PayloadAction<Filter>) => {
      state.sops.filters.type = action.payload
    },
    setSOPPageSize: (state, action: PayloadAction<number>) => {
      state.sops.pageSize = action.payload
    },
    setCaseListPageSize: (state, action: PayloadAction<number>) => {
      state.cases.pageSize = action.payload
    },
    setGuardToursPageSize: (state, action: PayloadAction<number>) => {
      state.guardTours.pageSize = action.payload
    },
    setGatewayDevicesNameLikeFilter: (state, action: PayloadAction<Filter>) => {
      state.gatewayDevices.filters.nameLike = action.payload
    },
    setGatewayDevicesPageSize: (state, action: PayloadAction<number>) => {
      state.gatewayDevices.pageSize = action.payload
    },
    setDevice: (state, action: PayloadAction<DevicePayload>) => {
      state.drawers.device.id = action.payload.id
      state.drawers.device.type = action.payload.type
    },
    setSOP: (state, action: PayloadAction<{ id: string }>) => {
      state.drawers.sop.id = action.payload.id
    },
    openIncidentDrawer: (state) => {
      state.drawers.incident.isOpen = true
    },
    closeIncidentDrawer: (state) => {
      state.drawers.incident.isOpen = false
      state.drawers.incident.isVideoPlaying = false
      state.drawers.incident.navigationContext =
        initialState.drawers.incident.navigationContext
    },
    setIncidentDrawerNavigationContext: (
      state,
      action: PayloadAction<IncidentDrawerNavigationContext>
    ) => {
      state.drawers.incident.navigationContext = action.payload
    },
    openDeviceDrawer: (state) => {
      state.drawers.device.isOpen = true
    },
    closeDeviceDrawer: (state) => {
      state.drawers.device.isOpen = false
    },
    openSOPModal: (state) => {
      state.drawers.sop.isOpen = true
    },
    closeSOPModal: (state) => {
      state.drawers.sop.isOpen = false
    },
    openSOPIncidentDrawer: (state) => {
      state.drawers.sopIncident.isOpen = true
    },
    closeSOPIncidentDrawer: (state) => {
      state.drawers.sopIncident.isOpen = false
      state.drawers.sopIncident.sopNameLike = null
      // Clear the detail selection too — a persisted id would otherwise reopen
      // the drawer on a previous incident's SOP detail (ENG-31544 Qodo review).
      state.drawers.sopIncident.id = null
    },
    toggleSOPIncidentDrawer: (state) => {
      const isClosing = state.drawers.sopIncident.isOpen
      state.drawers.sopIncident.isOpen = !state.drawers.sopIncident.isOpen
      if (isClosing) {
        state.drawers.sopIncident.sopNameLike = null
        state.drawers.sopIncident.id = null
      }
    },
    closeTraceLogDrawer: (state) => {
      state.drawers.incidentTraceLog.isOpen = false
      state.drawers.incidentTraceLog.highlightedEvent = null
      state.drawers.incidentTraceLog.deviceId = null
      state.drawers.incidentTraceLog.shouldForceDeviceTab = false
    },
    toggleIncidentTraceLogDrawer: (state) => {
      state.drawers.incidentTraceLog.isOpen =
        !state.drawers.incidentTraceLog.isOpen
      if (!state.drawers.incidentTraceLog.isOpen) {
        state.drawers.incidentTraceLog.highlightedEvent = null
        state.drawers.incidentTraceLog.deviceId = null
        state.drawers.incidentTraceLog.shouldForceDeviceTab = false
      }
    },
    openTraceLogDrawerWithHighlight: (
      state,
      action: PayloadAction<{ detectedAt: string; externalId: string }>
    ) => {
      state.drawers.incidentTraceLog.isOpen = true
      state.drawers.incidentTraceLog.highlightedEvent = action.payload
      // Pill deep-link keeps the default (Incident-tab) behavior.
      state.drawers.incidentTraceLog.deviceId = null
      state.drawers.incidentTraceLog.shouldForceDeviceTab = false
    },
    // Reasoning Trail "View access logs" deep-link: open filtered to a specific
    // device on the Device tab, optionally highlighting a qualifying event.
    openTraceLogDrawerForDevice: (
      state,
      action: PayloadAction<{
        deviceId: string
        highlightedEvent?: { detectedAt: string; externalId: string } | null
      }>
    ) => {
      state.drawers.incidentTraceLog.isOpen = true
      state.drawers.incidentTraceLog.deviceId = action.payload.deviceId
      state.drawers.incidentTraceLog.highlightedEvent =
        action.payload.highlightedEvent ?? null
      state.drawers.incidentTraceLog.shouldForceDeviceTab = true
    },
    setSOPIncidentId: (state, action: PayloadAction<{ id: string | null }>) => {
      state.drawers.sopIncident.id = action.payload.id
    },
    openGuardTourDrawer: (state) => {
      state.drawers.guardTour.isOpen = true
    },
    closeGuardTourDrawer: (state) => {
      state.drawers.guardTour.isOpen = false
    },
    setGuardTourId: (state, action: PayloadAction<{ id: string }>) => {
      state.drawers.guardTour.id = action.payload.id
    },
    openWorkflowDrawer: (state) => {
      if (!state.drawers.incidentWorkflow) {
        state.drawers.incidentWorkflow = { isOpen: false }
      }
      state.drawers.incidentWorkflow.isOpen = true
    },
    closeWorkflowDrawer: (state) => {
      if (!state.drawers.incidentWorkflow) {
        state.drawers.incidentWorkflow = { isOpen: false }
      }
      state.drawers.incidentWorkflow.isOpen = false
    },
    toggleWorkflowDrawer: (state) => {
      if (!state.drawers.incidentWorkflow) {
        state.drawers.incidentWorkflow = { isOpen: false }
      }
      state.drawers.incidentWorkflow.isOpen =
        !state.drawers.incidentWorkflow.isOpen
    },
    // The guards mirror the workflow reducers above: incidentMapPanel is an
    // additive field on the persisted ui slice, so state rehydrated from
    // before it existed won't have it.
    openIncidentMapPanelDrawer: (state) => {
      if (!state.drawers.incidentMapPanel) {
        state.drawers.incidentMapPanel = { isOpen: false }
      }
      state.drawers.incidentMapPanel.isOpen = true
    },
    closeIncidentMapPanelDrawer: (state) => {
      if (!state.drawers.incidentMapPanel) {
        state.drawers.incidentMapPanel = { isOpen: false }
      }
      state.drawers.incidentMapPanel.isOpen = false
    },
    toggleIncidentMapPanelDrawer: (state) => {
      if (!state.drawers.incidentMapPanel) {
        state.drawers.incidentMapPanel = { isOpen: false }
      }
      state.drawers.incidentMapPanel.isOpen =
        !state.drawers.incidentMapPanel.isOpen
    },
    setSOPIncidentNameLike: (state, action: PayloadAction<string | null>) => {
      state.drawers.sopIncident.sopNameLike = action.payload
    },
    expandEmergencyCall: (state) => {
      state.emergencyCall.isExpanded = true
    },
    minimizeEmergencyCall: (state) => {
      state.emergencyCall.isExpanded = false
    },
    muteEmergencyCall: (state) => {
      state.emergencyCall.isMuted = true
    },
    unmuteEmergencyCall: (state) => {
      state.emergencyCall.isMuted = false
    },
    toggleMuteEmergencyCall: (state) => {
      state.emergencyCall.isMuted = !state.emergencyCall.isMuted
    },
    openEmergencyCall: (state, action: PayloadAction<EmergencyCallPayload>) => {
      state.emergencyCall.isOpen = true
      state.emergencyCall.isExpanded = true
      state.emergencyCall.isMuted = false
      // Set incident details
      state.emergencyCall.incident.id = action.payload?.incidentId
      state.emergencyCall.incident.displayId = action.payload?.incidentDisplayId
      state.emergencyCall.incident.facilityName = action.payload?.facilityName
      state.emergencyCall.incident.emergencyNumber =
        action.payload?.emergencyNumber
    },
    closeEmergencyCall: (state) => {
      state.emergencyCall.isOpen = false
      state.emergencyCall.incident.id = null
      state.emergencyCall.incident.displayId = null
      state.emergencyCall.incident.facilityName = null
      state.emergencyCall.incident.emergencyNumber = null
    },
    setEmergencyCallDisplayIdLikeFilter: (
      state,
      action: PayloadAction<Filter>
    ) => {
      state.emergencyCall.filters.displayIdLike = action.payload
    },
    setEmergencyCallPageSize: (state, action: PayloadAction<number>) => {
      state.emergencyCall.pageSize = action.payload
    },
    setIncidentRulesPageSize: (state, action: PayloadAction<number>) => {
      state.incidentRules.pageSize = action.payload
    },
    setIncidentRulesNameLikeFilter: (state, action: PayloadAction<Filter>) => {
      state.incidentRules.filters.nameLike = action.payload
    },
    setIncidentRulesFacilityIdsFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.incidentRules.filters.facilityIds = action.payload
    },
    setIncidentRulesActionsFilter: (
      state,
      action: PayloadAction<MultiFilter<Action>>
    ) => {
      state.incidentRules.filters.actions = action.payload
    },
    setIncidentRulesStatusFilter: (
      state,
      action: PayloadAction<Filter<boolean>>
    ) => {
      state.incidentRules.filters.status = action.payload
    },
    saveFloorPlanIconSize: (
      state,
      action: PayloadAction<{ floorId: string; iconSize: number }>
    ) => {
      if (state.facility.iconSizes === undefined) {
        state.facility.iconSizes = {}
      }

      state.facility.iconSizes[action.payload.floorId] = action.payload.iconSize
    },
    setGuardToursFacilityIdsFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.guardTours.filters.facilityIds = action.payload
    },
    setGuardToursGuardIdsFilter: (
      state,
      action: PayloadAction<MultiFilter>
    ) => {
      state.guardTours.filters.guardIds = action.payload
    },
    setGuardToursStatusFilter: (state, action: PayloadAction<MultiFilter>) => {
      state.guardTours.filters.status = action.payload
    },
    setGuardToursSubmittedAtRangeFilter: (
      state,
      action: PayloadAction<SubmittedAtRangeFilter>
    ) => {
      state.guardTours.filters.submittedAtRange = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      const hydrateAction = action as PayloadAction<
        HydratePayload<UIStateIProps>
      >
      return {
        ...state,
        ...hydrateAction.payload.subject,
      }
    })
  },
})

export const {
  resetUi,
  setCompletedFtuIds,
  addCompletedFtuId,
  mergeCompletedFtuIds,
  toggleSideNavOpen,
  toggleNotificationsUnread,
  setVideoPlaying,
  enableEditMode,
  openFacilityFloorsDrawer,
  openFacilityDevicesDrawer,
  openFacilityCheckpointsDrawer,
  openFacilityTourPlansDrawer,
  setFacilityTabBarCount,
  toggleEditMode,
  setFacilityIdsFilter,
  setFacilityCreatedAtFilter,
  setFacilityHasIncidentsFilter,
  setDeviceFacilityIdsFilter,
  setDeviceFloorsIdsFilter,
  setDeviceFacilityCreatedAtFilter,
  setDevicesHasIncidentsFilter,
  setDeviceStatusFilter,
  setDeviceTypeFilter,
  setDeviceExternalAccountIdsFilter,
  setDeviceLabelIdsFilter,
  setDeviceAssociationStatusFilter,
  setDevicesPageSize,
  setIncident,
  setOutstandingIncidentsFacilitiesFilter,
  setOutstandingIncidentsIncidentTypesFilter,
  setOutstandingIncidentsDeviceTypeFilter,
  setOutstandingIncidentsGuardsFilter,
  setOutstandingIncidentsOperatorsFilter,
  setOutstandingIncidentsTimeOrderFilter,
  setOutstandingIncidentsPageSize,
  removeOutstandingIncidentFacility,
  removeOutstandingIncidentDeviceType,
  removeOutstandingIncidentIncidentTypes,
  removeOutstandingIncidentGuard,
  removeOutstandingIncidentOperator,
  removeOutstandingIncidentTimeOrder,
  clearOutstandingIncidentFilters,
  setDevice,
  setSOP,
  setSOPNameLikeFilter,
  setSOPFacilityIdFilter,
  setSOPCreatedAtFilter,
  setSOPCreatorIdFilter,
  setSOPTypeFilter,
  setSOPPageSize,
  setIncidentAudioAlert, // Fixed: Corrected the export name
  setIsDarkMode,
  setUserSettingsLoaded,
  setCaseListPageSize,
  setGuardToursPageSize,
  setGatewayDevicesNameLikeFilter,
  setGatewayDevicesPageSize,
  openIncidentDrawer,
  openDeviceDrawer,
  openSOPModal,
  closeIncidentDrawer,
  setIncidentDrawerNavigationContext,
  closeDeviceDrawer,
  closeSOPModal,
  openSOPIncidentDrawer,
  closeSOPIncidentDrawer,
  toggleSOPIncidentDrawer,
  closeTraceLogDrawer,
  toggleIncidentTraceLogDrawer,
  openTraceLogDrawerWithHighlight,
  openTraceLogDrawerForDevice,
  setSOPIncidentId,
  closeGuardTourDrawer,
  openGuardTourDrawer,
  setGuardTourId,
  openWorkflowDrawer,
  closeWorkflowDrawer,
  toggleWorkflowDrawer,
  openIncidentMapPanelDrawer,
  closeIncidentMapPanelDrawer,
  toggleIncidentMapPanelDrawer,
  setSOPIncidentNameLike,
  expandEmergencyCall,
  minimizeEmergencyCall,
  muteEmergencyCall,
  unmuteEmergencyCall,
  toggleMuteEmergencyCall,
  openEmergencyCall,
  closeEmergencyCall,
  setEmergencyCallDisplayIdLikeFilter,
  setEmergencyCallPageSize,
  setIncidentRulesPageSize,
  setIncidentRulesNameLikeFilter,
  setIncidentRulesFacilityIdsFilter,
  setIncidentRulesActionsFilter,
  setIncidentRulesStatusFilter,
  setCaseNameOrDisplayIdLike,
  setCaseStatusFilter,
  setCaseOwnerFilter,
  saveFloorPlanIconSize,
  setAiOperatorHubCreatedAtFilter,
  setGuardToursFacilityIdsFilter,
  setGuardToursGuardIdsFilter,
  setGuardToursStatusFilter,
  setGuardToursSubmittedAtRangeFilter,
} = uiSlice.actions

// region Selectors

// TODO - Move me to Selectors file!

export const selectUI = (state: AppState) => state.ui

export const selectSideNavOpen = (state: AppState) => state.ui.isSideNavOpen

export const selectEditMode = (state: AppState) => state.ui.isEditMode

export const selectIsVideoPlaying = (state: AppState) =>
  state.ui.drawers.incident.isVideoPlaying ?? false

export const selectNotificationsUnread = (state: AppState) =>
  state.ui.notifications.showUnread

export const selectIncidentAudioAlert = (state: AppState): boolean =>
  state.ui.notifications.incidentAudioAlert

export const selectIsDarkMode = (state: AppState): boolean =>
  state.ui.isDarkMode ?? true

export const selectUserSettingsLoaded = (state: AppState): boolean =>
  state.ui.userSettingsLoaded ?? false

export const selectCompletedFtuIds = (state: AppState): string[] =>
  state.ui.completedFtuIds ?? []

export const selectConfirmEditMode = (state: AppState) =>
  state.ui.hasConfirmedEditMode

export const selectFacilityFloorsDrawerOpen = (state: AppState) =>
  state.ui.facility.drawerOpen === 'floors'

export const selectCheckpointDrawerOpen = (state: AppState) =>
  state.ui.facility.drawerOpen === 'checkpoints'

export const selectFloorPlanIconSize = (state: AppState, floorId: string) => {
  const iconSizeMap: object = state?.ui?.facility?.iconSizes ?? {}
  const existingVal: number | undefined = iconSizeMap[floorId]
  return existingVal ? existingVal : DEFAULT_ICON_SIZE
}

// TODO - Would be nice to be able to match this against an enum and not raw strings
export const selectFacilityDrawerOpen = (state: AppState) =>
  state.ui.facility.drawerOpen

export const selectFacilityTabBarCount = (state: AppState) => ({
  placedDeviceCount: state.ui.facility.placedDeviceCount,
  floorCount: state.ui.facility.floorCount,
  checkpointCount: state.ui.facility.checkpointCount,
  tourPlansCount: state.ui.facility.tourPlansCount,
})

export const selectFacilityIdsFilter = (state: AppState) =>
  state.ui.facilities?.filters?.ids

export const selectCreatedAtFilter = (state: AppState) =>
  state.ui.facilities?.filters?.createdAtRange

export const selectFacilityHasIncidentsFilter = (state: AppState) =>
  state.ui.facilities?.filters?.hasIncidents

export const facilityFilterCountSelector = createSelector(
  selectFacilityIdsFilter,
  selectCreatedAtFilter,
  selectFacilityHasIncidentsFilter,
  (idsFilter, createdAtFilter, hasIncidentsFilter) => {
    const ids = idsFilter?.length || 0
    const createdAt = createdAtFilter ? 1 : 0
    const hasIncidents = hasIncidentsFilter ? 1 : 0

    return ids + createdAt + hasIncidents
  }
)

export const selectDeviceFacilityIdsFilter = (state: AppState) =>
  state.ui.devices?.filters?.facilityIds

export const selectDeviceFloorsIdsFilter = (state: AppState) =>
  state.ui.devices?.filters?.floorIds

export const selectDeviceCreatedAtFilter = (state: AppState) =>
  state.ui.devices?.filters?.createdAtRange

export const selectDeviceHasIncidentsFilter = (state: AppState) =>
  state.ui.devices?.filters?.hasIncidents

export const selectDeviceStatusFilter = (state: AppState) =>
  state.ui.devices?.filters?.deviceStatus

export const selectDeviceTypeFilter = (state: AppState) =>
  state.ui.devices?.filters?.deviceType

export const selectDeviceExternalAccountIdsFilter = (state: AppState) =>
  state.ui.devices?.filters?.externalAccountIds

export const selectDeviceLabelIdsFilter = (state: AppState) =>
  state.ui.devices?.filters?.labelIds

export const selectDeviceAssociationStatusFilter = (state: AppState) =>
  state.ui.devices?.filters?.associationStatus ??
  initialState.devices.filters.associationStatus

export const deviceFilterCountSelector = createSelector(
  selectDeviceFacilityIdsFilter,
  selectDeviceFloorsIdsFilter,
  selectDeviceCreatedAtFilter,
  selectDeviceHasIncidentsFilter,
  selectDeviceStatusFilter,
  selectDeviceTypeFilter,
  selectDeviceExternalAccountIdsFilter,
  selectDeviceLabelIdsFilter,
  selectDeviceAssociationStatusFilter,
  (
    facilityIdsFilter,
    deviceIdsFilter,
    createdAtFilter,
    hasIncidentsFilter,
    deviceStatusFilter,
    deviceTypeFilter,
    deviceExternalAccountIdsFilter,
    deviceLabelIdsFilter,
    associationStatusFilter
  ) => {
    const facilityIds = facilityIdsFilter?.length || 0
    const floorIds = deviceIdsFilter?.length || 0
    const createdAt = createdAtFilter ? 1 : 0
    const hasIncidents = hasIncidentsFilter ? 1 : 0
    const deviceStatus = deviceStatusFilter ? 1 : 0
    const deviceType = deviceTypeFilter?.length || 0
    const deviceExternalAccountIds = deviceExternalAccountIdsFilter?.length || 0
    const labelIds = deviceLabelIdsFilter?.length || 0
    const associationStatus = associationStatusFilter ? 1 : 0

    return (
      facilityIds +
      floorIds +
      createdAt +
      hasIncidents +
      deviceStatus +
      deviceType +
      deviceExternalAccountIds +
      labelIds +
      associationStatus
    )
  }
)

export const selectOutstandingIncidentsFacilitiesFilter = (state: AppState) =>
  state.ui.outstandingIncidents?.filters?.facilities

export const selectOutstandingIncidentsDeviceTypeFilter = (state: AppState) =>
  state.ui.outstandingIncidents?.filters?.deviceType

export const selectOutstandingIncidentsIncidentTypeFilter = (state: AppState) =>
  state.ui.outstandingIncidents?.filters?.incidentTypes

export const selectOutstandingIncidentsGuardsFilter = (state: AppState) =>
  state.ui.outstandingIncidents?.filters?.guards

export const selectOutstandingIncidentsOperatorsFilter = (state: AppState) =>
  state.ui.outstandingIncidents?.filters?.operators

export const selectOutstandingIncidentsTimeOrderFilter = (state: AppState) =>
  state.ui.outstandingIncidents?.filters?.timeOrder

export const selectOutstandingIncidentsPageSize = (state: AppState) =>
  state.ui.outstandingIncidents?.pageSize

export const selectIsIncidentDrawerOpen = (state: AppState) =>
  state.ui.drawers?.incident?.isOpen

export const selectIsDeviceDrawerOpen = (state: AppState) =>
  state.ui.drawers?.device?.isOpen

export const selectIsSOPModalOpen = (state: AppState) =>
  state.ui.drawers?.sop?.isOpen

export const selectGuardTourOpen = (state: AppState) =>
  state.ui.drawers?.guardTour?.isOpen

export const selectIncident = (state: AppState) => ({
  id: state.ui.drawers.incident.id,
})

export const selectIncidentDrawerNavigationContext = (state: AppState) =>
  state.ui.drawers.incident.navigationContext

export const selectGuardTour = (state: AppState) => ({
  id: state.ui.drawers.guardTour.id,
})
// Memoized like selectSOP: DeviceDrawer is mounted for the whole session, so an
// unmemoized object literal here would fail useSelector's reference check on
// every dispatch app-wide and re-render it each time.
export const selectDevice = createSelector(
  (state: AppState) => state.ui.drawers.device.id,
  (state: AppState) => state.ui.drawers.device.type,
  (id, type) => ({ id, type })
)

export const selectSOP = createSelector(
  (state: AppState) => state.ui.drawers.sop.id,
  (id) => ({ id })
)
export const selectSOPNameLikeFilter = (state: AppState) =>
  state.ui.sops?.filters?.nameLike

export const selectSOPFacilityIdFilter = (state: AppState) =>
  state.ui.sops?.filters?.facilityId

export const selectSOPCreatedAtFilter = (state: AppState) =>
  state.ui.sops?.filters?.createdAtRange

export const selectSOPCreatorIdFilter = (state: AppState) =>
  state.ui.sops?.filters?.creatorId

export const selectSOPTypeFilter = (state: AppState) =>
  state.ui?.sops?.filters?.type

export const selectSOPPageSize = (state: AppState) => state.ui.sops?.pageSize

export const selectCaseListPageSize = (state: AppState) =>
  state.ui.cases?.pageSize

export const selectGuardToursPageSize = (state: AppState) =>
  state.ui.guardTours?.pageSize

export const selectGuardToursFacilityIdsFilter = (state: AppState) =>
  state.ui.guardTours?.filters?.facilityIds

export const selectGuardToursGuardIdsFilter = (state: AppState) =>
  state.ui.guardTours?.filters?.guardIds

export const selectGuardToursStatusFilter = (state: AppState) =>
  state.ui.guardTours?.filters?.status

export const selectGuardToursSubmittedAtRangeFilter = (state: AppState) =>
  state.ui.guardTours?.filters?.submittedAtRange

export const guardToursFilterCountSelector = createSelector(
  selectGuardToursFacilityIdsFilter,
  selectGuardToursGuardIdsFilter,
  selectGuardToursStatusFilter,
  selectGuardToursSubmittedAtRangeFilter,
  (facilityIdsFilter, guardIdsFilter, statusFilter, submittedAtFilter) => {
    const facilityIds = facilityIdsFilter?.length ? 1 : 0
    const guardIds = guardIdsFilter?.length ? 1 : 0
    const status = statusFilter?.length ? 1 : 0
    const submittedAt = submittedAtFilter ? 1 : 0

    return facilityIds + guardIds + status + submittedAt
  }
)

export const selectGatewayDevicesNameLikeFilter = (state: AppState) =>
  state.ui.gatewayDevices?.filters?.nameLike

export const selectGatewayDevicesPageSize = (state: AppState) =>
  state.ui.gatewayDevices?.pageSize

export const selectDevicesPageSize = (state: AppState) =>
  state.ui.devices?.pageSize

export const sopFilterCountSelector = createSelector(
  selectSOPFacilityIdFilter,
  selectSOPCreatedAtFilter,
  selectSOPCreatorIdFilter,
  selectSOPTypeFilter,
  (facilityIdsFilter, createdAtFilter, creatorIdFilter, typeFilter) => {
    const facilityId = facilityIdsFilter ? 1 : 0
    const createdAt = createdAtFilter ? 1 : 0
    const creatorId = creatorIdFilter ? 1 : 0
    const type = typeFilter ? 1 : 0

    return facilityId + createdAt + creatorId + type
  }
)

export const selectIncidentTraceLogDrawerState = (state: AppState) =>
  state.ui.drawers?.incidentTraceLog

export const selectSOPIncidentDrawerState = (state: AppState) =>
  state.ui.drawers?.sopIncident

export const selectWorkflowDrawerIsOpen = (state: AppState) =>
  state.ui.drawers?.incidentWorkflow?.isOpen ?? false

export const selectIncidentMapPanelDrawerIsOpen = (state: AppState) =>
  state.ui.drawers?.incidentMapPanel?.isOpen ?? false

export const selectEmergencyCall = (state: AppState) =>
  state.ui?.emergencyCall ?? initialState.emergencyCall

export const selectEmergencyCallIncident = (state: AppState) =>
  selectEmergencyCall(state).incident

export const selectEmergencyCallDisplayIdLikeFilter = (state: AppState) =>
  selectEmergencyCall(state).filters?.displayIdLike

export const selectEmergencyCallPageSize = (state: AppState) =>
  selectEmergencyCall(state).pageSize

export const selectIncidentRulesPageSize = (state: AppState) =>
  state.ui.incidentRules?.pageSize

export const selectIncidentRulesNameLikeFilter = (state: AppState) =>
  state.ui.incidentRules?.filters?.nameLike

export const selectIncidentRulesFacilityIdsFilter = (state: AppState) =>
  state.ui.incidentRules?.filters?.facilityIds

export const selectIncidentRulesActionsFilter = (state: AppState) =>
  state.ui.incidentRules?.filters?.actions

export const selectIncidentRulesStatusFilter = (state: AppState) =>
  state.ui.incidentRules?.filters?.status

export const incidentRulesFilterCountSelector = createSelector(
  selectIncidentRulesFacilityIdsFilter,
  selectIncidentRulesActionsFilter,
  selectIncidentRulesStatusFilter,
  (facilityIdsFilter, actionsFilter, statusFilter) => {
    const facilityIds = facilityIdsFilter?.length || 0
    const actions = actionsFilter?.length || 0
    const status = statusFilter ? 1 : 0

    return facilityIds + actions + status
  }
)

export const selectCaseNameOrDisplayIdLike = (state: AppState) =>
  state.ui.cases?.filters?.nameOrDisplayIdLike

export const selectCaseStatusFilter = (state: AppState) =>
  state.ui.cases?.filters?.status

export const selectCaseOwnerFilter = (state: AppState) =>
  state.ui.cases?.filters?.owner

export const caseFilterCountSelector = createSelector(
  selectCaseStatusFilter,
  selectCaseOwnerFilter,
  (statusFilter, ownerFilter) => {
    const status = statusFilter?.length ? 1 : 0
    const owner = ownerFilter ? 1 : 0

    return status + owner
  }
)

export const selectAiOperatorHubCreatedAtFilter = (state: AppState) =>
  state.ui.aiOperatorHub?.filters?.createdAtRange

// endregion

// Default export for reducers is a pattern recommended by Redux
// See here: <https://redux.js.org/tutorials/fundamentals/part-3-state-actions-reducers#combinereducers>
// eslint-disable-next-line import/no-default-export
export default uiSlice.reducer
